/**
 * Helpers for RTK Query mutation rejections (FetchBaseQueryError / SerializedError).
 * Plain objects stringify as "[object Object]" — never use String(err) for UI/Sentry.
 */

export function formatMutationError(err, fallback = "Request failed") {
  const detail = err?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail != null && typeof detail === "object") {
    // Platform structured errors (e.g. foreign_recipients) expose a human
    // `message` — prefer that over dumping the raw JSON blob into the UI.
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message.trim();
    }
    if (typeof detail.error === "string" && detail.error.trim()) {
      const code = detail.error.trim();
      if (code === "foreign_recipients" && Array.isArray(detail.rejected)) {
        const rejected = detail.rejected.filter(Boolean).join(", ");
        return rejected
          ? `Recipients are not members of this website: ${rejected}`
          : "Recipients are not members of this website.";
      }
      return code;
    }
    try {
      return JSON.stringify(detail);
    } catch {
      // fall through
    }
  }
  const status = err?.status;
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR") {
    return "Request timed out or was interrupted. Please retry.";
  }
  if (status === 504) {
    return "Request timed out. Please retry.";
  }
  if (typeof err?.error === "string" && err.error.trim()) return err.error;
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  if (typeof status === "number") {
    return `${fallback} (HTTP ${status})`;
  }
  return fallback;
}

export function shouldReportMutationFailure(err) {
  const status = err?.status;
  // Network / router drops and client/4xx responses already surface in the UI;
  // reporting them as Sentry exceptions creates noise without actionable frames.
  if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR" || status === "PARSING_ERROR") {
    return false;
  }
  if (typeof status === "number" && (status === 504 || status < 500)) {
    return false;
  }
  return true;
}

/** Strip accidental whitespace/tabs from platform website IDs (paste/DB noise). */
export function normalizeWebsiteId(websiteId) {
  if (websiteId == null) return websiteId;
  if (typeof websiteId !== "string") return websiteId;
  const trimmed = websiteId.trim();
  return trimmed.length ? trimmed : websiteId;
}

/**
 * Platform create returns 409 when the scan_id already exists.
 * Only then fall back to update — other failures (e.g. 404 website not found)
 * must not be treated as "scan exists".
 */
export function isScanAlreadyExistsError(err) {
  return err?.status === 409;
}

export function toCaptureError(err, fallback) {
  if (err instanceof Error) return err;
  return new Error(formatMutationError(err, fallback));
}
